import {
  Connection,
  Keypair,
  PublicKey,
  SystemProgram,
  LAMPORTS_PER_SOL,
  TransactionMessage,
  VersionedTransaction,
} from "@solana/web3.js";

import {
  TOKEN_2022_PROGRAM_ID,
  ASSOCIATED_TOKEN_PROGRAM_ID,
  ExtensionType,
  AccountState,
  getMintLen,
  TYPE_SIZE,
  LENGTH_SIZE,
  createInitializeMint2Instruction,
  createInitializeTransferFeeConfigInstruction,
  createInitializeMetadataPointerInstruction,
  createInitializeNonTransferableMintInstruction,
  createInitializePermanentDelegateInstruction,
  createInitializeDefaultAccountStateInstruction,
  createInitializeMintCloseAuthorityInstruction,
  createInitializePausableConfigInstruction,
  createInitializeScaledUiAmountConfigInstruction,
  createTransferCheckedWithFeeInstruction,
  getAssociatedTokenAddressSync,
  createAssociatedTokenAccountIdempotentInstruction,
  createMintToInstruction,
  getTransferFeeAmount,
  unpackAccount,
  harvestWithheldTokensToMint,
  createHarvestWithheldTokensToMintInstruction,
  withdrawWithheldTokensFromMint,
  getMint,
  getAccount,
} from "@solana/spl-token";

import {
  createInitializeInstruction,
  createUpdateFieldInstruction,
  pack,
} from "@solana/spl-token-metadata";

// ─────────────────────────────────────────────────────────────────────────────
// CONSTANTS
// ─────────────────────────────────────────────────────────────────────────────

const PLATFORM_FEE_ENABLED = true;
const PLATFORM_FEE_WALLET_RAW =
  process.env.NEXT_PUBLIC_PLATFORM_FEE || process.env.NEXT_PLATFORM_FEE;

/** 0.1 SOL — stored as BigInt throughout to avoid any float rounding */
const PLATFORM_FEE_LAMPORTS = BigInt(100_000_000); // 0.1 * 1e9 exactly

function parsePlatformFeeWallet() {
  const value = String(PLATFORM_FEE_WALLET_RAW ?? "").trim();
  if (!value) {
    throw new Error("Missing platform fee wallet. Set NEXT_PUBLIC_PLATFORM_FEE.");
  }
  try {
    return new PublicKey(value);
  } catch {
    throw new Error(
      "Invalid platform fee wallet in NEXT_PUBLIC_PLATFORM_FEE."
    );
  }
}

function normalizePlatformFeeLamports(value) {
  if (value == null) return PLATFORM_FEE_LAMPORTS;
  try {
    const lamports = BigInt(value);
    if (lamports < 0n) return PLATFORM_FEE_LAMPORTS;
    return lamports;
  } catch {
    return PLATFORM_FEE_LAMPORTS;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// PRIVATE UTILITIES  (not exported)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Convert a percentage (0–100) to Token-2022 basis points (0–10 000).
 * Throws a RangeError for out-of-range values.
 * @param {number} feePercent
 * @returns {number}
 */
function percentToFeeBasisPoints(feePercent) {
  const bp = Math.round(feePercent * 100);
  if (bp < 0 || bp > 10_000) {
    throw new RangeError(
      `feePercent must be 0–100 (got ${feePercent}). ` +
      `Token-2022 supports a maximum of 100 % (10 000 basis points).`
    );
  }
  return bp;
}

/**
 * Convert whole-token amounts to raw (atomic) units using integer BigInt math.
 * Avoids the silent precision loss of `Math.round(tokens * 10 ** decimals)`.
 * @param {number} wholeTokens
 * @param {number} decimals
 * @returns {bigint}
 */
function tokensToRaw(wholeTokens, decimals) {
  // Split into integer and fractional parts to preserve precision
  const factor = 10n ** BigInt(decimals);
  const intPart = BigInt(Math.trunc(wholeTokens));
  const fracPart = BigInt(Math.round((wholeTokens % 1) * Number(factor)));
  return intPart * factor + fracPart;
}

/**
 * Returns true when a Uint8Array signature slot contains a real signature
 * (i.e. not all-zero placeholder bytes).
 * @param {Uint8Array | null | undefined} sig
 * @returns {boolean}
 */
function hasNonZeroSignature(sig) {
  if (!sig || sig.length === 0) return false;
  return sig.some((b) => b !== 0);
}

/**
 * Find signer index in a VersionedTransaction signature array.
 * @param {VersionedTransaction} tx
 * @param {PublicKey} signer
 * @returns {number}
 */
function getSignerIndex(tx, signer) {
  return tx.message.staticAccountKeys.findIndex((k) => k.equals(signer));
}

/**
 * Compute the withheld fee for a raw transfer amount, respecting the cap.
 * Pure function — no async, no I/O.
 * @param {bigint} rawAmount
 * @param {number} feeBasisPoints
 * @param {bigint} maxFeeRaw
 * @returns {bigint}
 */
function computeWithheldFee(rawAmount, feeBasisPoints, maxFeeRaw) {
  const calculated = (rawAmount * BigInt(feeBasisPoints)) / 10_000n;
  return calculated > maxFeeRaw ? maxFeeRaw : calculated;
}

// ─────────────────────────────────────────────────────────────────────────────
// PUBLIC UTILITIES
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Create a confirmed-commitment Connection.
 * @param {string} rpcUrl
 * @returns {Connection}
 */
function createConnection(rpcUrl) {
  return new Connection(rpcUrl, "confirmed");
}

/**
 * Preview how much tax will be withheld on a given transfer.
 * Useful for showing "you will receive X" in the UI before signing.
 *
 * @param {{ transferAmountTokens: number, feePercent: number, maxFee: number, decimals: number }} params
 * @returns {{ feeTokens: number, feeRaw: bigint, recipientReceivesTokens: number }}
 */
function calculateExpectedTransferFee({
  transferAmountTokens,
  feePercent,
  maxFee,
  decimals,
}) {
  const rawAmount = tokensToRaw(transferAmountTokens, decimals);
  const feeBasisPoints = percentToFeeBasisPoints(feePercent);
  const maxFeeRaw = tokensToRaw(maxFee, decimals);
  const feeRaw = computeWithheldFee(rawAmount, feeBasisPoints, maxFeeRaw);
  const feeTokens = Number(feeRaw) / 10 ** decimals;
  return {
    feeTokens,
    feeRaw,
    recipientReceivesTokens: transferAmountTokens - feeTokens,
  };
}

async function buildCreateTaxTokenTransaction({
  connection,
  payerPublicKey,
  mintKeypair,
  tokenConfig,
}) {
  const {
    name,
    symbol,
    uri,
    decimals,
    enableTransferFee = true,
    enableNonTransferable = false,
    enablePermanentDelegate = false,
    permanentDelegate = null,
    enableDefaultAccountState = false,
    defaultAccountState = "initialized",
    enableMintCloseAuthority = false,
    mintCloseAuthority = null,
    enablePausable = false,
    pausableAuthority = null,
    enableScaledUiAmount = false,
    scaledUiAmountMultiplier = 1,
    feePercent,
    maxFee,
    authorityWallet,
    withdrawAuthority,
    additionalMetadata = [],
    platformFeeLamports = PLATFORM_FEE_LAMPORTS,
  } = tokenConfig;

  const feeBasisPoints = enableTransferFee
    ? percentToFeeBasisPoints(feePercent)
    : 0;
  const maxFeeRaw = enableTransferFee ? tokensToRaw(maxFee, decimals) : 0n;
  const resolvedPlatformFeeLamports = normalizePlatformFeeLamports(
    platformFeeLamports,
  );

  const hasExtensionType = (key) =>
    Object.prototype.hasOwnProperty.call(ExtensionType, key) &&
    typeof ExtensionType[key] === "number";

  // ── Compute exact mint account size ────────────────────────────────────────
  // getMintLen returns the base mint size + extension *headers* for each
  // listed extension type. We add the metadata *payload* size on top.
  const tokenMetadataObj = {
    updateAuthority: payerPublicKey,
    mint: mintKeypair.publicKey,
    name,
    symbol,
    uri,
    additionalMetadata,
  };
  // For compatibility with current Token-2022 + token-metadata flows:
  // - create the mint account with base space (fixed extensions only)
  // - pre-fund rent for full eventual size (base + metadata TLV payload)
  // - metadata initialize instruction will handle account growth path
  const tokenMetadataLen = TYPE_SIZE + LENGTH_SIZE + pack(tokenMetadataObj).length;
  const canUseTransferFee =
    enableTransferFee &&
    hasExtensionType("TransferFeeConfig") &&
    typeof createInitializeTransferFeeConfigInstruction === "function";
  const canUseNonTransferable =
    enableNonTransferable &&
    hasExtensionType("NonTransferable") &&
    typeof createInitializeNonTransferableMintInstruction === "function";
  const canUsePermanentDelegate =
    enablePermanentDelegate &&
    hasExtensionType("PermanentDelegate") &&
    typeof createInitializePermanentDelegateInstruction === "function";
  const canUseDefaultAccountState =
    enableDefaultAccountState &&
    hasExtensionType("DefaultAccountState") &&
    typeof createInitializeDefaultAccountStateInstruction === "function";
  const canUseMintCloseAuthority =
    enableMintCloseAuthority &&
    hasExtensionType("MintCloseAuthority") &&
    typeof createInitializeMintCloseAuthorityInstruction === "function";
  const canUsePausable =
    enablePausable &&
    hasExtensionType("Pausable") &&
    typeof createInitializePausableConfigInstruction === "function";
  const canUseScaledUiAmount =
    enableScaledUiAmount &&
    hasExtensionType("ScaledUiAmount") &&
    typeof createInitializeScaledUiAmountConfigInstruction === "function";

  const mintExtensions = [ExtensionType.MetadataPointer];
  if (canUseTransferFee) mintExtensions.unshift(ExtensionType.TransferFeeConfig);
  if (canUseNonTransferable) mintExtensions.push(ExtensionType.NonTransferable);
  if (canUsePermanentDelegate) mintExtensions.push(ExtensionType.PermanentDelegate);
  if (canUseDefaultAccountState) mintExtensions.push(ExtensionType.DefaultAccountState);
  if (canUseMintCloseAuthority) mintExtensions.push(ExtensionType.MintCloseAuthority);
  if (canUsePausable) mintExtensions.push(ExtensionType.Pausable);
  if (canUseScaledUiAmount) mintExtensions.push(ExtensionType.ScaledUiAmount);
  const mintBaseLen = getMintLen(mintExtensions);
  const mintRentLen = mintBaseLen + tokenMetadataLen;

  const [mintLamports, { blockhash, lastValidBlockHeight }] = await Promise.all([
    connection.getMinimumBalanceForRentExemption(mintRentLen),
    connection.getLatestBlockhash("confirmed"),
  ]);

  // ── Instructions ───────────────────────────────────────────────────────────
  // RULE: all extension InitializeXxx instructions MUST precede InitializeMint.

  // ① Platform fee (visible in Phantom preview) - only when enabled by env
  const platformFeeWallet = PLATFORM_FEE_ENABLED
    ? parsePlatformFeeWallet()
    : null;
  const platformFeeIx = PLATFORM_FEE_ENABLED
    ? SystemProgram.transfer({
      fromPubkey: payerPublicKey,
      toPubkey: platformFeeWallet,
      lamports: resolvedPlatformFeeLamports,
    })
    : null;

  // ② Allocate rent-exempt mint account owned by Token-2022
  const createMintAccountIx = SystemProgram.createAccount({
    fromPubkey: payerPublicKey,
    newAccountPubkey: mintKeypair.publicKey,
    space: mintBaseLen,
    lamports: mintLamports,
    programId: TOKEN_2022_PROGRAM_ID,
  });

  // ③ Extension: TransferFeeConfig (optional)
  const transferFeeConfigIx = canUseTransferFee
    ? createInitializeTransferFeeConfigInstruction(
      mintKeypair.publicKey,
      authorityWallet, // can update fee % and cap
      withdrawAuthority, // can sweep collected taxes
      feeBasisPoints,
      maxFeeRaw,
      TOKEN_2022_PROGRAM_ID
    )
    : null;

  const nonTransferableIx = canUseNonTransferable
    ? createInitializeNonTransferableMintInstruction(
      mintKeypair.publicKey,
      TOKEN_2022_PROGRAM_ID,
    )
    : null;

  const permanentDelegateIx = canUsePermanentDelegate
    ? createInitializePermanentDelegateInstruction(
      mintKeypair.publicKey,
      permanentDelegate,
      TOKEN_2022_PROGRAM_ID,
    )
    : null;

  const defaultAccountStateIx = canUseDefaultAccountState
    ? createInitializeDefaultAccountStateInstruction(
      mintKeypair.publicKey,
      defaultAccountState === "frozen"
        ? AccountState.Frozen
        : AccountState.Initialized,
      TOKEN_2022_PROGRAM_ID,
    )
    : null;

  const mintCloseAuthorityIx = canUseMintCloseAuthority
    ? createInitializeMintCloseAuthorityInstruction(
      mintKeypair.publicKey,
      mintCloseAuthority,
      TOKEN_2022_PROGRAM_ID,
    )
    : null;

  const pausableConfigIx = canUsePausable
    ? createInitializePausableConfigInstruction(
      mintKeypair.publicKey,
      pausableAuthority,
      TOKEN_2022_PROGRAM_ID,
    )
    : null;

  const scaledUiAmountIx = canUseScaledUiAmount
    ? createInitializeScaledUiAmountConfigInstruction(
      mintKeypair.publicKey,
      payerPublicKey,
      Number(scaledUiAmountMultiplier),
      TOKEN_2022_PROGRAM_ID,
    )
    : null;

  // ④ Extension: MetadataPointer — metadata lives on the mint account itself
  const metadataPointerIx = createInitializeMetadataPointerInstruction(
    mintKeypair.publicKey,
    payerPublicKey,        // metadata update authority
    mintKeypair.publicKey, // metadata account == mint
    TOKEN_2022_PROGRAM_ID
  );

  // ⑤ Init the mint (must come AFTER all extension inits)
  const initMintIx = createInitializeMint2Instruction(
    mintKeypair.publicKey,
    decimals,
    payerPublicKey, // mint authority
    canUseDefaultAccountState && defaultAccountState === "frozen"
      ? payerPublicKey
      : null,
    TOKEN_2022_PROGRAM_ID
  );

  // ⑥ Write base metadata (name / symbol / URI)
  const initMetadataIx = createInitializeInstruction({
    programId: TOKEN_2022_PROGRAM_ID,
    metadata: mintKeypair.publicKey,
    updateAuthority: payerPublicKey,
    mint: mintKeypair.publicKey,
    mintAuthority: payerPublicKey,
    name,
    symbol,
    uri,
  });

  // ⑦ Write any additional key/value pairs
  const additionalMetadataIxs = additionalMetadata.map(([field, value]) =>
    createUpdateFieldInstruction({
      programId: TOKEN_2022_PROGRAM_ID,
      metadata: mintKeypair.publicKey,
      updateAuthority: payerPublicKey,
      field,
      value,
    })
  );

  const message = new TransactionMessage({
    payerKey: payerPublicKey,
    recentBlockhash: blockhash,
    instructions: [
      ...(platformFeeIx ? [platformFeeIx] : []),
      createMintAccountIx,
      ...(transferFeeConfigIx ? [transferFeeConfigIx] : []),
      ...(nonTransferableIx ? [nonTransferableIx] : []),
      ...(permanentDelegateIx ? [permanentDelegateIx] : []),
      ...(defaultAccountStateIx ? [defaultAccountStateIx] : []),
      ...(mintCloseAuthorityIx ? [mintCloseAuthorityIx] : []),
      ...(pausableConfigIx ? [pausableConfigIx] : []),
      ...(scaledUiAmountIx ? [scaledUiAmountIx] : []),
      metadataPointerIx,
      initMintIx,
      initMetadataIx,
      ...additionalMetadataIxs,
    ],
  }).compileToV0Message();

  return {
    transaction: new VersionedTransaction(message),
    mintKeypair,
    blockhash,
    lastValidBlockHeight,
    mintLamports,
    feeBasisPoints,
    maxFeeRaw,
  };
}

/**
 * Full create-token flow: build → balance check → wallet sign → mint sign →
 * simulate → broadcast → confirm.
 *
 * @param {{
 *   connection: Connection,
 *   walletAdapter: import("@solana/wallet-adapter-base").WalletAdapter,
 *   tokenConfig: Parameters<typeof buildCreateTaxTokenTransaction>[0]["tokenConfig"],
 * }} params
 * @returns {Promise<{ mintAddress: string, signature: string, feeBasisPoints: number, maxFeeRaw: bigint }>}
 */
async function signAndSendCreateTaxToken({ connection, walletAdapter, tokenConfig }) {
  if (!walletAdapter?.publicKey) {
    throw new Error("Wallet not connected — call wallet.connect() first.");
  }

  const mintKeypair = Keypair.generate();

  const {
    transaction,
    blockhash,
    lastValidBlockHeight,
    mintLamports,
    feeBasisPoints,
    maxFeeRaw,
  } = await buildCreateTaxTokenTransaction({
    connection,
    payerPublicKey: walletAdapter.publicKey,
    mintKeypair,
    tokenConfig,
  });

  // ── Balance guard ──────────────────────────────────────────────────────────
  const payerBalance = await connection.getBalance(walletAdapter.publicKey, "processed");
  // 0.005 SOL safety buffer covers tx fees + any minor rent estimate variance
  const requiredLamports =
    (PLATFORM_FEE_ENABLED ? Number(PLATFORM_FEE_LAMPORTS) : 0) +
    mintLamports +
    Math.round(0.005 * LAMPORTS_PER_SOL);

  if (payerBalance < requiredLamports) {
    const need = (requiredLamports / LAMPORTS_PER_SOL).toFixed(4);
    const have = (payerBalance / LAMPORTS_PER_SOL).toFixed(4);
    throw new Error(
      `Insufficient SOL. Need ~${need} SOL (rent${PLATFORM_FEE_ENABLED ? " + platform fee" : ""} + tx buffer), have ${have} SOL.`
    );
  }

  // ── Step 1: User/Phantom wallet signs first ───────────────────────────────
  const signedTx = await walletAdapter.signTransaction(transaction);
  const payerSigIndex = getSignerIndex(signedTx, walletAdapter.publicKey);
  if (payerSigIndex < 0 || !hasNonZeroSignature(signedTx.signatures[payerSigIndex])) {
    throw new Error("Wallet signature missing after wallet sign.");
  }

  // ── Step 2: Mint keypair partial-signs after wallet ───────────────────────
  signedTx.sign([mintKeypair]);
  const mintSigIndex = getSignerIndex(signedTx, mintKeypair.publicKey);
  if (mintSigIndex < 0 || !hasNonZeroSignature(signedTx.signatures[mintSigIndex])) {
    throw new Error("Mint keypair signature missing after mint sign.");
  }

  // Confirm both required signatures are filled (payer index 0, mint index 1)
  const allSigned = signedTx.signatures.every(hasNonZeroSignature);
  if (!allSigned) {
    throw new Error(
      "Not all required signatures are present after wallet sign."
    );
  }

  // ── Step 3: Simulate before paying for a failed tx ────────────────────────
  const { value: simResult } = await connection.simulateTransaction(signedTx, {
    commitment: "processed",
    sigVerify: true,
  });

  if (simResult.err) {
    const logs = simResult.logs?.join("\n") ?? "No logs";
    throw new Error(
      `Token creation simulation failed: ${JSON.stringify(simResult.err)}\n\nLogs:\n${logs}`
    );
  }

  // ── Step 4: Broadcast ─────────────────────────────────────────────────────
  const signature = await connection.sendRawTransaction(signedTx.serialize(), {
    skipPreflight: false,
    preflightCommitment: "confirmed",
    maxRetries: 3,
  });

  await connection.confirmTransaction(
    { signature, blockhash, lastValidBlockHeight },
    "confirmed"
  );

  return {
    mintAddress: mintKeypair.publicKey.toBase58(),
    signature,
    feeBasisPoints,
    maxFeeRaw,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// MINTING
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Build a transaction that creates the destination ATA (idempotent) and mints
 * `amountTokens` to it.
 *
 * @param {{
 *   connection: Connection,
 *   payerPublicKey: PublicKey,
 *   mintAddress: PublicKey,
 *   destinationOwner: PublicKey,
 *   amountTokens: number,
 *   decimals: number,
 * }} params
 * @returns {Promise<{ transaction: VersionedTransaction, blockhash: string, lastValidBlockHeight: number }>}
 */
async function buildMintTokensTransaction({
  connection,
  payerPublicKey,
  mintAddress,
  destinationOwner,
  amountTokens,
  decimals,
}) {
  const rawAmount = tokensToRaw(amountTokens, decimals);

  const destinationAta = getAssociatedTokenAddressSync(
    mintAddress,
    destinationOwner,
    false,
    TOKEN_2022_PROGRAM_ID,
    ASSOCIATED_TOKEN_PROGRAM_ID
  );

  const { blockhash, lastValidBlockHeight } =
    await connection.getLatestBlockhash("confirmed"); // "confirmed" is fast enough for UI

  const message = new TransactionMessage({
    payerKey: payerPublicKey,
    recentBlockhash: blockhash,
    instructions: [
      createAssociatedTokenAccountIdempotentInstruction(
        payerPublicKey,
        destinationAta,
        destinationOwner,
        mintAddress,
        TOKEN_2022_PROGRAM_ID,
        ASSOCIATED_TOKEN_PROGRAM_ID
      ),
      createMintToInstruction(
        mintAddress,
        destinationAta,
        payerPublicKey, // mint authority
        rawAmount,
        [],
        TOKEN_2022_PROGRAM_ID
      ),
    ],
  }).compileToV0Message();

  return {
    transaction: new VersionedTransaction(message),
    blockhash,
    lastValidBlockHeight,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// TRANSFERS
// ─────────────────────────────────────────────────────────────────────────────


async function buildTransferWithTaxTransaction({
  connection,
  payerPublicKey,
  sourceOwner,
  mintAddress,
  destinationOwner,
  amountTokens,
  decimals,
  feePercent,
  maxFee,
}) {
  const rawAmount = tokensToRaw(amountTokens, decimals);
  const feeBasisPoints = percentToFeeBasisPoints(feePercent);
  const maxFeeRaw = tokensToRaw(maxFee, decimals);
  const withheldFeeRaw = computeWithheldFee(rawAmount, feeBasisPoints, maxFeeRaw);
  const withheldFeeTokens = Number(withheldFeeRaw) / 10 ** decimals;

  const sourceAta = getAssociatedTokenAddressSync(
    mintAddress, sourceOwner, false,
    TOKEN_2022_PROGRAM_ID, ASSOCIATED_TOKEN_PROGRAM_ID
  );
  const destinationAta = getAssociatedTokenAddressSync(
    mintAddress, destinationOwner, false,
    TOKEN_2022_PROGRAM_ID, ASSOCIATED_TOKEN_PROGRAM_ID
  );

  const { blockhash, lastValidBlockHeight } =
    await connection.getLatestBlockhash("confirmed");

  const message = new TransactionMessage({
    payerKey: payerPublicKey,
    recentBlockhash: blockhash,
    instructions: [
      createAssociatedTokenAccountIdempotentInstruction(
        payerPublicKey, destinationAta, destinationOwner, mintAddress,
        TOKEN_2022_PROGRAM_ID, ASSOCIATED_TOKEN_PROGRAM_ID
      ),
      createTransferCheckedWithFeeInstruction(
        sourceAta,
        mintAddress,
        destinationAta,
        sourceOwner,
        rawAmount,
        decimals,
        withheldFeeRaw,
        [],
        TOKEN_2022_PROGRAM_ID
      ),
    ],
  }).compileToV0Message();

  return {
    transaction: new VersionedTransaction(message),
    withheldFeeTokens,
    recipientReceivesTokens: amountTokens - withheldFeeTokens,
    blockhash,
    lastValidBlockHeight,
  };
}

/**
 * Build a transaction to harvest withheld transfer fees from token accounts
 * into the mint account (permissionless operation in Token-2022).
 *
 * @param {{
 *   connection: Connection,
 *   payerPublicKey: PublicKey,
 *   mintAddress: PublicKey,
 *   tokenAccounts: PublicKey[],
 * }} params
 * @returns {Promise<{ transaction: VersionedTransaction, blockhash: string, lastValidBlockHeight: number }>}
 */
async function buildHarvestTaxFeesToMintTransaction({
  connection,
  payerPublicKey,
  mintAddress,
  tokenAccounts,
}) {
  const { blockhash, lastValidBlockHeight } =
    await connection.getLatestBlockhash("confirmed");

  const harvestIx = createHarvestWithheldTokensToMintInstruction(
    mintAddress,
    tokenAccounts,
    TOKEN_2022_PROGRAM_ID
  );

  const message = new TransactionMessage({
    payerKey: payerPublicKey,
    recentBlockhash: blockhash,
    instructions: [harvestIx],
  }).compileToV0Message();

  return {
    transaction: new VersionedTransaction(message),
    blockhash,
    lastValidBlockHeight,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// TAX COLLECTION
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Step 1 of tax collection: harvest withheld fees from token accounts into
 * the mint account. Run this before `withdrawCollectedTax`.
 *
 * @param {{ connection: Connection, payer: Keypair, mintAddress: PublicKey, tokenAccounts: PublicKey[] }} params
 */
async function harvestTaxFeesToMint({ connection, payer, mintAddress, tokenAccounts }) {
  return harvestWithheldTokensToMint(
    connection, payer, mintAddress, tokenAccounts,
    { commitment: "confirmed" },
    TOKEN_2022_PROGRAM_ID
  );
}

/**
 * Step 2 of tax collection: sweep fees from the mint into a destination ATA.
 * Only the `withdrawAuthority` wallet can call this.
 *
 * @param {{
 *   connection: Connection,
 *   payer: Keypair,
 *   mintAddress: PublicKey,
 *   destinationAta: PublicKey,
 *   withdrawAuthority: Keypair | PublicKey,
 * }} params
 */
async function withdrawCollectedTax({
  connection, payer, mintAddress, destinationAta, withdrawAuthority,
}) {
  return withdrawWithheldTokensFromMint(
    connection, payer, mintAddress, destinationAta, withdrawAuthority,
    [], { commitment: "confirmed" },
    TOKEN_2022_PROGRAM_ID
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// READ HELPERS
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Sum pending (un-harvested) withheld fees across a list of token accounts.
 * Accounts that are not Token-2022 or have no fee data are skipped silently.
 *
 * @param {{ connection: Connection, tokenAccounts: (PublicKey | string)[], decimals: number }} params
 * @returns {Promise<{ totalRaw: bigint, totalTokens: number, skipped: number }>}
 */
async function getPendingWithheldFees({ connection, tokenAccounts, decimals }) {
  const pubkeys = tokenAccounts.map((a) =>
    a instanceof PublicKey ? a : new PublicKey(a)
  );

  const infos = await connection.getMultipleAccountsInfo(pubkeys);
  let totalRaw = 0n;
  let skipped = 0;

  for (let i = 0; i < infos.length; i++) {
    const info = infos[i];
    if (!info) { skipped++; continue; }

    try {
      const account = unpackAccount(pubkeys[i], info, TOKEN_2022_PROGRAM_ID);
      const feeAmount = getTransferFeeAmount(account);
      if (feeAmount?.withheldAmount) {
        totalRaw += feeAmount.withheldAmount;
      }
    } catch {
      // Not a Token-2022 account or no TransferFee extension — skip
      skipped++;
    }
  }

  return {
    totalRaw,
    totalTokens: Number(totalRaw) / 10 ** decimals,
    skipped, // surfaced so callers know if any accounts were unreadable
  };
}

/**
 * Fetch mint info (decimals, supply, extensions, etc.) for a Token-2022 mint.
 * @param {Connection} connection
 * @param {PublicKey | string} mintAddress
 */
async function getMintInfo(connection, mintAddress) {
  const pk = mintAddress instanceof PublicKey ? mintAddress : new PublicKey(mintAddress);
  return getMint(connection, pk, "confirmed", TOKEN_2022_PROGRAM_ID);
}

/**
 * Fetch a Token-2022 token account.
 * @param {Connection} connection
 * @param {PublicKey | string} tokenAccountAddress
 */
async function getTokenAccountInfo(connection, tokenAccountAddress) {
  const pk =
    tokenAccountAddress instanceof PublicKey
      ? tokenAccountAddress
      : new PublicKey(tokenAccountAddress);
  return getAccount(connection, pk, "confirmed", TOKEN_2022_PROGRAM_ID);
}

// ─────────────────────────────────────────────────────────────────────────────
// EXPORTS
// ─────────────────────────────────────────────────────────────────────────────

const tokenService = {
  // Utilities
  createConnection,
  calculateExpectedTransferFee,

  // Token creation
  buildCreateTaxTokenTransaction, // low-level: returns unsigned tx + mintKeypair
  signAndSendCreateTaxToken,      // high-level: build + Phantom sign + send

  // Minting supply
  buildMintTokensTransaction,

  // Tax-aware transfers
  buildTransferWithTaxTransaction,

  // Tax collection (two-step: harvest → withdraw)
  buildHarvestTaxFeesToMintTransaction,
  harvestTaxFeesToMint,
  withdrawCollectedTax,

  // Read / query helpers
  getMintInfo,
  getTokenAccountInfo,
  getPendingWithheldFees,
};

export default tokenService;